import { InfoSet } from "../InfoSets/InfoSet";
import { PortList } from "../Core/PortList";
import { Device } from "../Core/Device";
import { Dictionary } from "../Utils/Dictionary";
import { ObjectSerializer } from "../Serialization/ObjectSerializer";
import { ObjSerUtils } from "../Serialization/ObjSerUtils";
import { ValueEncoder } from "../Utils/ValueEncoder";

export class VlanEntry {
	public Index: number;

	public Name: string;
	public get HasName():boolean {
		return !!this.Name;
	}

	public get DisplayName(): string {
		return this.Name || "VLAN " + this.Index;
	}

	public get DisplayNameWithIndex(): string {
		let displayName = '';
		if (this.HasName) {
			if (this.Index <= 0)
				displayName = this.Name;
			else
				displayName = this.Name + " [" + this.Index + "]";
		}
		else {
			displayName = "VLAN" + " [" + this.Index + "]";
		}
		return displayName;
	}


	// PORT Membership
	public EgressPorts: PortList;
	public UntaggedPorts: PortList;
	public ForbiddenPorts: PortList;

	public IfIndex: number;
	public get HasIfIndex():boolean {
		return this.IfIndex < 0;
	}

	constructor(vlanIndex?: number) {
		this.Index = vlanIndex;
		this.Name = '';

		this.EgressPorts = new PortList();
		this.UntaggedPorts = new PortList();
		this.ForbiddenPorts = new PortList();

		this.IfIndex = -1;
	}

	public IsReservedVLAN(device: Device): boolean {
		var isReserved = false;

		if (this.Index <= 1)
			isReserved = true;
		else if (this.Index >= VlanTable.DefaultGVRP_VLAN)
			isReserved = true;
		else if (device != null) {
			// CHECK the range of CISCO reserved VLAN IDs
			if (device.IsCiscoDevice) {
				if ((this.Index >= 1002) && (this.Index <= 1005))
					isReserved = true;
			}
		}

		return (isReserved);
	}

	public HasPort(portIndex: number): boolean {
		if (portIndex <= 0)
			return false;

		return this.EgressPorts.HasPort(portIndex) || this.UntaggedPorts.HasPort(portIndex);
	}

	public HasPorts(): boolean {
		return this.EgressPorts.HasPorts || this.UntaggedPorts.HasPorts;
	}

	public IsTaggedPort(portIndex: number): boolean {
		return this.EgressPorts.HasPort(portIndex) && !this.UntaggedPorts.HasPort(portIndex);
	}

	public IsUntaggedPort(portIndex: number): boolean {
		return this.UntaggedPorts.HasPort(portIndex);
	}

	public Write(obj: any, context: any): void {
		obj.vlan_entry = {};
		obj = obj.vlan_entry;

		obj.vlan_index = ValueEncoder.EncodeInteger(this.Index);

		if (this.HasName)
			obj.name = this.Name;

		let portMembership = '';

		portMembership = this.EgressPorts.toString();
		if (portMembership)
			obj.egress_ports = portMembership;

		portMembership = this.UntaggedPorts.toString();
		if (portMembership)
			obj.untagged_ports = portMembership;

		portMembership = this.ForbiddenPorts.toString();
		if (portMembership)
			obj.forbidden_ports = portMembership;

		if (this.HasIfIndex)
			obj.if_index = ValueEncoder.EncodeInteger(this.IfIndex);
	}

	public Read(obj: any, context: any): void {
		if (!obj.vlan_entry)
			throw new Error();
		obj = obj.vlan_entry;

		if (obj.vlan_index !== undefined)
			this.Index = ValueEncoder.DecodeInteger(obj.vlan_index);

		if (obj.name !== undefined)
			this.Name = obj.name;

		if (obj.egress_ports !== undefined)
			this.EgressPorts.SetPorts(obj.egress_ports);

		if (obj.untagged_ports !== undefined)
			this.UntaggedPorts.SetPorts(obj.untagged_ports);

		if (obj.forbidden_ports !== undefined)
			this.ForbiddenPorts.SetPorts(obj.forbidden_ports);

		if (obj.if_index !== undefined)
			this.IfIndex = ValueEncoder.DecodeInteger(obj.if_index);
	}
}

export class VlanTable extends InfoSet
{
	public static DefaultGVRP_VLAN = 4093;

	private _vlanTable: Dictionary<number, VlanEntry> ;

	constructor(device: Device) {
		super(device);
		this._vlanTable = new Dictionary<number, VlanEntry>();
	}

	public Add(vlanIndex: number): VlanEntry {
		var vlanEntry: VlanEntry = this._vlanTable.Item(vlanIndex);
			
		if(!vlanEntry) {
			vlanEntry = new VlanEntry(vlanIndex);
			this._vlanTable.Add(vlanIndex, vlanEntry);
		}

		return vlanEntry;
	}

	private AddEntry(vlanEntry: VlanEntry): void {
		this._vlanTable.Add(vlanEntry.Index, vlanEntry);
	}

	public Clear(): void {
		this._vlanTable.Clear();
	}

	public get Count(): number {
		return this._vlanTable.Count;
	}

	public Find(vlanIndex: number): VlanEntry {
		return this._vlanTable.Item(vlanIndex);
	}

	public FindByIfIndex(ifIndex: number): VlanEntry {
		var foundEntry: VlanEntry = null;

		this._vlanTable.Some((pair) => {
			if (pair.Value.IfIndex == ifIndex) {
				foundEntry = pair.Value;
				return true;
			}
		});

		return foundEntry;
	}

	public RemoveByIndex(bpIndex: number): boolean {
		return this._vlanTable.Remove(bpIndex);
	}

	public Remove(vlanEntry: VlanEntry):boolean {
		if (vlanEntry)
			return this._vlanTable.Remove(vlanEntry.Index);
		
		return false;
	}

	public GetFirstNonReservedVlanIndex(device: Device): number {
		var vlanIndex = -1;

		if (this._vlanTable) {
			var isCiscoDevice:boolean = device.IsCiscoDevice;
				
			this._vlanTable.Some((pair) => {
				var vlanEntry: VlanEntry = pair.Value;
				if (vlanEntry.Index <= 1 || vlanEntry.Index >= VlanTable.DefaultGVRP_VLAN)
					return;

				if (isCiscoDevice && vlanEntry.Index >= 1002 && vlanEntry.Index <= 1005)
					return;

				vlanIndex = vlanEntry.Index;
				return true;
			});
		}

		return (vlanIndex);
	}

	public HasPortMembers(): boolean {
		return this._vlanTable.Some((pair) => {
			return pair.Value.HasPorts();
		});
	}

	public IsVlanTrunkPort(bpIndex: number): boolean {
		return this._vlanTable.Some((pair) => {
			var vlanEntry: VlanEntry = pair.Value; 
			// IGNORE any VLANs that MIGHT be RESERVED (other than DEFAULT=1)
			if (vlanEntry.Index > 1 && vlanEntry.IsReservedVLAN(this.Device))
					return;
	
			// CHECK to see if VLAN is being TAGGED on this PORT (bpIndex)
			if (vlanEntry.EgressPorts.HasPort(bpIndex)) {
				// VERIFY that this VLAN is not being sent UNTAGGED on this PORT (bpInde)
				if (!vlanEntry.UntaggedPorts.HasPort(bpIndex)) {
					return true;
				}
			}
		});
	}

	public VlansTrunkedOnPort(bpIndex: number): string {
		var vlanList = new Array<number>();

		this._vlanTable.ForEach((pair) => {
			if (pair.Value.IsTaggedPort(bpIndex))
				vlanList.push(pair.Value.Index);
		});

		if (vlanList.length > 0)
			return '< ' + vlanList.join(', ') + ' >';
		else
			return '';
	}

	public FindUntaggedVlanForPort(bpIndex: number): number {
		var vlanIndex = -1;

		this._vlanTable.Some((pair) => {
			if (!pair.Value.UntaggedPorts || !pair.Value.UntaggedPorts.HasPort(bpIndex))
				return;

			vlanIndex = pair.Value.Index;
			return true;;
		});

		return (vlanIndex);
	}

	public ForEach(callbackfn: (value: VlanEntry, index: number, array: VlanEntry[]) => void, thisArg?: any): void {
		this._vlanTable.Values().ForEach(callbackfn, thisArg);
	}

	public Some(callbackfn: (value: VlanEntry, index: number, array: VlanEntry[]) => boolean, thisArg?: any): boolean {
		return this._vlanTable.Values().Some(callbackfn, thisArg);
	}

	public Write(obj: any, context: any): void {
		obj.vlan_info = {};
		obj = obj.vlan_info;

		obj.entries = [];
		ObjSerUtils.WriteObjSerCollection(obj.entries, this._vlanTable.Values(), context);
	}

	public Read(obj: any, context: any): void {
		if (!obj.vlan_info)
			throw new Error();
		obj = obj.vlan_info;

		this.Clear();

		if (obj.entries !== undefined) {
			obj.entries.forEach((value: any) => {
				let entry = new VlanEntry();
				entry.Read(value, context);
				this.AddEntry(entry);
			}, this);
		}
	}
}
